/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * API for managing meeting bots, calendar integrations, and webhooks
 * OpenAPI spec version: 2.0.0
 */
import { z as zod } from "zod"

/**
 * Create a new Zoom credential for your team.

    Zoom credentials store your Zoom OAuth App credentials (client_id and client_secret) securely encrypted. You can create two types of credentials:

    **App-only credentials:** Provide only `name`, `client_id`, and `client_secret`. These credentials can be used for SDK authentication when bots join meetings.

    **User-authorized credentials:** Additionally provide `authorization_code` and `redirect_uri`. The API will exchange the authorization code for OAuth tokens, enabling OBF (On-Behalf-Of) token support. OBF tokens allow bots to join meetings on behalf of a specific Zoom user. The authorising user's `zoom_email` and `zoom_display_name` are captured from Zoom's `/users/me` API at exchange time (requires the `user:read:user` scope) and returned in the response so you can show end users which Zoom account is connected.

    **Custom Metadata:** Pass an optional `extra` JSON object to tag the credential with your own key-value pairs (for example an internal user ID, environment, or tenant). The data is stored as-is, returned by all credential endpoints, and is filterable on the list endpoint via the `extra` query parameter.

    **Security:** All credentials are encrypted at rest using AES-256-GCM. Client secrets and OAuth tokens are never returned in API responses.

    **OAuth Flow:** To obtain an authorization code, redirect users to Zoom's OAuth authorization endpoint and capture the code from the callback. Ensure your redirect URI exactly matches the one registered in your Zoom OAuth App.

    **Error Scenarios:**
    - `400 Bad Request`: Invalid input or missing redirect_uri when authorization_code is provided
    - `400 Bad Request`: Failed to exchange authorization code (invalid code or redirect_uri mismatch)
 * @summary Create a Zoom credential
 */
export const createZoomCredentialBodyNameMax = 100
export const createZoomCredentialBodyClientIdMax = 256
export const createZoomCredentialBodyClientSecretMax = 256
export const createZoomCredentialBodyAuthorizationCodeMax = 2000

export const createZoomCredentialBody = zod.object({
  name: zod
    .string()
    .min(1)
    .max(createZoomCredentialBodyNameMax)
    .describe(
      "A user-friendly name for the credential.\n\nThis name is for your reference and can be anything that helps you identify this credential. Names do not need to be unique."
    ),
  client_id: zod
    .string()
    .min(1)
    .max(createZoomCredentialBodyClientIdMax)
    .describe(
      "The Client ID from your Zoom OAuth App.\n\nFound in your Zoom Marketplace app under 'App Credentials'. This is used for SDK authentication when joining meetings."
    ),
  client_secret: zod
    .string()
    .min(1)
    .max(createZoomCredentialBodyClientSecretMax)
    .describe(
      "The Client Secret from your Zoom OAuth App.\n\nFound in your Zoom Marketplace app under 'App Credentials'. This will be encrypted at rest and never returned in API responses."
    ),
  authorization_code: zod
    .string()
    .max(createZoomCredentialBodyAuthorizationCodeMax)
    .optional()
    .describe(
      "OAuth authorization code obtained from Zoom OAuth flow.\n\nWhen provided, the API will exchange this code for access and refresh tokens, making this a 'user' type credential that supports OBF (On-Behalf-Of) tokens.\n\n**Important:** `redirect_uri` is required when providing an authorization code."
    ),
  redirect_uri: zod
    .string()
    .url()
    .optional()
    .describe(
      "The redirect URI used in the OAuth flow.\n\nMust exactly match the redirect URI registered in your Zoom OAuth App and used when obtaining the authorization code."
    ),
  extra: zod
    .record(zod.string(), zod.any())
    .describe(
      "An optional free-form JSON object you can attach to the credential.\n\nThis is stored as-is and never interpreted by the API. Useful for tagging credentials with your own identifiers (e.g. internal user ID, environment, tenant) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
    )
    .or(zod.null())
    .optional()
})

/**
 * List all Zoom credentials for your team.

    Returns all non-deleted credentials with their metadata. Sensitive fields (client_secret, OAuth tokens) are never included in responses.

    **Response Fields:**
    - `credential_id`: UUID to reference this credential in bot requests
    - `name`: User-friendly name for identification
    - `credential_type`: "app" (SDK only) or "user" (with OAuth tokens)
    - `zoom_user_id`: The Zoom user ID (only for "user" type)
    - `zoom_email` / `zoom_display_name`: The authorising Zoom user's email and display name, captured from Zoom's `/users/me` API at OAuth time (only for "user" type, requires the `user:read:user` scope)
    - `state`: "active" or "invalid"
    - `last_error_message`: Last OBF token fetch error (if any)
    - `extra`: The optional user-supplied JSON metadata attached to the credential

    **Filtering:** Narrow the result set with optional query parameters (combined with AND):
    - `name`, `zoom_email`, `zoom_display_name`: case-insensitive partial match
    - `zoom_user_id`: exact match
    - `credential_type`, `state`: comma-separated enum lists (e.g. `credential_type=user`, `state=active,invalid`)
    - `extra`: `key:value` pairs against the `extra` JSON payload, comma-separated for multiple conditions (e.g. `extra=internal_user_id:u_42,environment:production`). Values are matched exactly (case-sensitive); credentials missing the key are excluded.

    **Error Tracking:** If bots fail to fetch OBF tokens using a credential, the error is recorded in `last_error_message` and `last_error_at`. These fields are cleared on successful OBF token fetch.
 * @summary List Zoom credentials
 */
export const listZoomCredentialsQueryNameDefault = null
export const listZoomCredentialsQueryZoomEmailDefault = null
export const listZoomCredentialsQueryZoomDisplayNameDefault = null
export const listZoomCredentialsQueryZoomUserIdDefault = null
export const listZoomCredentialsQueryExtraDefault = null

export const listZoomCredentialsQueryParams = zod.object({
  name: zod
    .string()
    .describe(
      "Filter credentials whose `name` contains this string (case-insensitive partial match)."
    )
    .or(zod.null())
    .optional(),
  zoom_email: zod
    .string()
    .describe(
      "Filter credentials whose `zoom_email` contains this string (case-insensitive partial match).\n\nUseful when a user has connected multiple Zoom accounts and you want to find a specific one."
    )
    .or(zod.null())
    .optional(),
  zoom_display_name: zod
    .string()
    .describe(
      "Filter credentials whose `zoom_display_name` contains this string (case-insensitive partial match)."
    )
    .or(zod.null())
    .optional(),
  zoom_user_id: zod
    .string()
    .describe("Filter credentials by exact `zoom_user_id`.")
    .or(zod.null())
    .optional(),
  credential_type: zod
    .string()
    .or(zod.null())
    .describe("Filter by credential type. Comma-separated list. Valid values: `app`, `user`.")
    .or(zod.null())
    .optional(),
  state: zod
    .string()
    .or(zod.null())
    .describe(
      "Filter by credential state. Comma-separated list. Valid values: `active`, `invalid`."
    )
    .or(zod.null())
    .optional(),
  extra: zod
    .string()
    .describe(
      'Filter credentials by matching values in the `extra` JSON payload.\n\nApplies SQL-level filtering on the `extra` field and reduces the result set to credentials that match all specified conditions.\n\nFormat:\n- Single condition: `\"field:value\"`\n- Multiple conditions: `\"field1:value1,field2:value2\"`\n\nExamples:\n- `internal_user_id:u_123` — only credentials with this internal user ID\n- `environment:production,tenant:acme` — only production credentials for the acme tenant\n\nNotes: all conditions must match; values are matched exactly (case-sensitive); credentials without the field are excluded.'
    )
    .or(zod.null())
    .optional()
})

export const listZoomCredentialsResponseDataItemCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/
export const listZoomCredentialsResponseDataItemNameMax = 100
export const listZoomCredentialsResponseDataItemLastErrorAtRegExpOne =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/
export const listZoomCredentialsResponseDataItemCreatedAtRegExp =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/
export const listZoomCredentialsResponseDataItemUpdatedAtRegExp =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/

export const listZoomCredentialsResponse = zod.object({
  success: zod.literal(true),
  data: zod.array(
    zod.object({
      credential_id: zod
        .string()
        .uuid()
        .regex(listZoomCredentialsResponseDataItemCredentialIdRegExp)
        .describe("The unique identifier (UUID) of the Zoom credential"),
      name: zod
        .string()
        .min(1)
        .max(listZoomCredentialsResponseDataItemNameMax)
        .describe(
          "A user-friendly name for the credential.\n\nThis name is for your reference and can be anything that helps you identify this credential. Names do not need to be unique."
        ),
      credential_type: zod
        .enum(["app", "user"])
        .describe(
          "The type of Zoom credential.\n\n- `app`: SDK-only credentials (client_id and client_secret) for joining meetings\n- `user`: User-authorized credentials with OAuth tokens for OBF token support"
        ),
      zoom_user_id: zod
        .string()
        .describe(
          "The Zoom user ID associated with this credential.\n\nOnly present for 'user' type credentials. This is the Zoom user who authorized the OAuth app."
        )
        .or(zod.null()),
      zoom_account_id: zod
        .string()
        .describe(
          "The Zoom account ID associated with this credential.\n\nOnly present for 'user' type credentials."
        )
        .or(zod.null()),
      zoom_email: zod
        .string()
        .describe(
          "The email address of the Zoom user who authorised this credential.\n\nCaptured from Zoom's `/users/me` API at the time of OAuth exchange (or refresh) using the `user:read:user` scope. Use this to display which Zoom account is connected when a user has multiple accounts.\n\nOnly present for 'user' type credentials."
        )
        .or(zod.null()),
      zoom_display_name: zod
        .string()
        .describe(
          "The display name of the Zoom user who authorised this credential.\n\nCaptured from Zoom's `/users/me` API at the time of OAuth exchange (or refresh).\n\nOnly present for 'user' type credentials."
        )
        .or(zod.null()),
      scopes: zod
        .string()
        .describe(
          "The OAuth scopes granted to this credential.\n\nOnly present for 'user' type credentials. Comma-separated list of Zoom OAuth scopes."
        )
        .or(zod.null()),
      state: zod
        .enum(["active", "invalid"])
        .describe(
          "The current state of the Zoom credential.\n\n- `active`: Credential is working and can be used\n- `invalid`: Credential has failed (tokens revoked, expired, etc.)"
        ),
      last_error_message: zod
        .string()
        .describe(
          "The last error message from OBF token fetch attempts.\n\nThis is set when a bot fails to fetch an OBF token using this credential. It is cleared when a successful OBF token fetch occurs."
        )
        .or(zod.null()),
      last_error_at: zod
        .string()
        .datetime({})
        .regex(listZoomCredentialsResponseDataItemLastErrorAtRegExpOne)
        .describe(
          "When the last OBF token fetch error occurred (ISO 8601).\n\nCleared when a successful OBF token fetch occurs."
        )
        .or(zod.null()),
      created_at: zod
        .string()
        .datetime({})
        .regex(listZoomCredentialsResponseDataItemCreatedAtRegExp)
        .describe("When this credential was created (ISO 8601)"),
      updated_at: zod
        .string()
        .datetime({})
        .regex(listZoomCredentialsResponseDataItemUpdatedAtRegExp)
        .describe("When this credential was last updated (ISO 8601)"),
      extra: zod
        .record(zod.string(), zod.any())
        .describe(
          "An optional free-form JSON object you can attach to the credential.\n\nThis is stored as-is and never interpreted by the API. Useful for tagging credentials with your own identifiers (e.g. internal user ID, environment, tenant) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
        )
        .or(zod.null())
    })
  )
})

/**
 * Get detailed information about a specific Zoom credential.

    Returns the credential's metadata including its current state and any recent errors. Sensitive fields (client_secret, OAuth tokens) are never included.

    For "user" type credentials, the response also includes `zoom_email` and `zoom_display_name` (captured from Zoom's `/users/me` API at OAuth time) and any `extra` JSON metadata you attached at creation or via `PATCH`.

    **Error Tracking:** The `last_error_message` and `last_error_at` fields show the most recent OBF token fetch failure. Check these fields if bots using this credential are failing to join meetings.

    Returns 404 if the credential is not found or does not belong to your team.
 * @summary Get a Zoom credential
 */
export const getZoomCredentialPathIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const getZoomCredentialParams = zod.object({
  id: zod
    .string()
    .uuid()
    .regex(getZoomCredentialPathIdRegExp)
    .describe("The unique identifier (UUID) of the Zoom credential")
})

export const getZoomCredentialResponseDataCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/
export const getZoomCredentialResponseDataNameMax = 100
export const getZoomCredentialResponseDataLastErrorAtRegExpOne =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/
export const getZoomCredentialResponseDataCreatedAtRegExp =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/
export const getZoomCredentialResponseDataUpdatedAtRegExp =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/

export const getZoomCredentialResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    credential_id: zod
      .string()
      .uuid()
      .regex(getZoomCredentialResponseDataCredentialIdRegExp)
      .describe("The unique identifier (UUID) of the Zoom credential"),
    name: zod
      .string()
      .min(1)
      .max(getZoomCredentialResponseDataNameMax)
      .describe(
        "A user-friendly name for the credential.\n\nThis name is for your reference and can be anything that helps you identify this credential. Names do not need to be unique."
      ),
    credential_type: zod
      .enum(["app", "user"])
      .describe(
        "The type of Zoom credential.\n\n- `app`: SDK-only credentials (client_id and client_secret) for joining meetings\n- `user`: User-authorized credentials with OAuth tokens for OBF token support"
      ),
    zoom_user_id: zod
      .string()
      .describe(
        "The Zoom user ID associated with this credential.\n\nOnly present for 'user' type credentials. This is the Zoom user who authorized the OAuth app."
      )
      .or(zod.null()),
    zoom_account_id: zod
      .string()
      .describe(
        "The Zoom account ID associated with this credential.\n\nOnly present for 'user' type credentials."
      )
      .or(zod.null()),
    zoom_email: zod
      .string()
      .describe(
        "The email address of the Zoom user who authorised this credential.\n\nCaptured from Zoom's `/users/me` API at the time of OAuth exchange (or refresh) using the `user:read:user` scope. Use this to display which Zoom account is connected when a user has multiple accounts.\n\nOnly present for 'user' type credentials."
      )
      .or(zod.null()),
    zoom_display_name: zod
      .string()
      .describe(
        "The display name of the Zoom user who authorised this credential.\n\nCaptured from Zoom's `/users/me` API at the time of OAuth exchange (or refresh).\n\nOnly present for 'user' type credentials."
      )
      .or(zod.null()),
    scopes: zod
      .string()
      .describe(
        "The OAuth scopes granted to this credential.\n\nOnly present for 'user' type credentials. Comma-separated list of Zoom OAuth scopes."
      )
      .or(zod.null()),
    state: zod
      .enum(["active", "invalid"])
      .describe(
        "The current state of the Zoom credential.\n\n- `active`: Credential is working and can be used\n- `invalid`: Credential has failed (tokens revoked, expired, etc.)"
      ),
    last_error_message: zod
      .string()
      .describe(
        "The last error message from OBF token fetch attempts.\n\nThis is set when a bot fails to fetch an OBF token using this credential. It is cleared when a successful OBF token fetch occurs."
      )
      .or(zod.null()),
    last_error_at: zod
      .string()
      .datetime({})
      .regex(getZoomCredentialResponseDataLastErrorAtRegExpOne)
      .describe(
        "When the last OBF token fetch error occurred (ISO 8601).\n\nCleared when a successful OBF token fetch occurs."
      )
      .or(zod.null()),
    created_at: zod
      .string()
      .datetime({})
      .regex(getZoomCredentialResponseDataCreatedAtRegExp)
      .describe("When this credential was created (ISO 8601)"),
    updated_at: zod
      .string()
      .datetime({})
      .regex(getZoomCredentialResponseDataUpdatedAtRegExp)
      .describe("When this credential was last updated (ISO 8601)"),
    extra: zod
      .record(zod.string(), zod.any())
      .describe(
        "An optional free-form JSON object you can attach to the credential.\n\nThis is stored as-is and never interpreted by the API. Useful for tagging credentials with your own identifiers (e.g. internal user ID, environment, tenant) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
      )
      .or(zod.null())
  })
})

/**
 * Update an existing Zoom credential.

    You can update the credential name, SDK credentials (client_id/client_secret), the user-supplied `extra` metadata, or re-authorize with new OAuth tokens.

    **Updating Name:** Provide only `name` to rename the credential.

    **Updating SDK Credentials:** Provide both `client_id` and `client_secret` together to update the SDK credentials.

    **Updating Custom Metadata:** Provide `extra` to replace the credential's metadata payload, or send `"extra": null` to clear it.

    **Re-authorizing:** Provide `authorization_code`, `redirect_uri`, `client_id`, and `client_secret` to exchange a new authorization code for fresh OAuth tokens. This also refreshes `zoom_email` and `zoom_display_name` from Zoom's `/users/me` API, resets the credential state to "active", and clears any error messages.

    **Error Scenarios:**
    - `400 Bad Request`: Missing redirect_uri when authorization_code is provided
    - `400 Bad Request`: Failed to exchange authorization code
    - `404 Not Found`: Credential not found or does not belong to your team
 * @summary Update a Zoom credential
 */
export const updateZoomCredentialPathIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const updateZoomCredentialParams = zod.object({
  id: zod
    .string()
    .uuid()
    .regex(updateZoomCredentialPathIdRegExp)
    .describe("The unique identifier (UUID) of the Zoom credential")
})

export const updateZoomCredentialBodyNameMax = 100
export const updateZoomCredentialBodyClientIdMax = 256
export const updateZoomCredentialBodyClientSecretMax = 256
export const updateZoomCredentialBodyAuthorizationCodeMax = 2000

export const updateZoomCredentialBody = zod.object({
  name: zod
    .string()
    .min(1)
    .max(updateZoomCredentialBodyNameMax)
    .optional()
    .describe(
      "A user-friendly name for the credential.\n\nThis name is for your reference and can be anything that helps you identify this credential. Names do not need to be unique."
    ),
  client_id: zod
    .string()
    .min(1)
    .max(updateZoomCredentialBodyClientIdMax)
    .optional()
    .describe(
      "The Client ID from your Zoom OAuth App.\n\nFound in your Zoom Marketplace app under 'App Credentials'. This is used for SDK authentication when joining meetings."
    ),
  client_secret: zod
    .string()
    .min(1)
    .max(updateZoomCredentialBodyClientSecretMax)
    .optional()
    .describe(
      "The Client Secret from your Zoom OAuth App.\n\nFound in your Zoom Marketplace app under 'App Credentials'. This will be encrypted at rest and never returned in API responses."
    ),
  authorization_code: zod
    .string()
    .max(updateZoomCredentialBodyAuthorizationCodeMax)
    .optional()
    .describe(
      "OAuth authorization code obtained from Zoom OAuth flow.\n\nWhen provided, the API will exchange this code for access and refresh tokens, making this a 'user' type credential that supports OBF (On-Behalf-Of) tokens.\n\n**Important:** `redirect_uri` is required when providing an authorization code."
    ),
  redirect_uri: zod
    .string()
    .url()
    .optional()
    .describe(
      "The redirect URI used in the OAuth flow.\n\nMust exactly match the redirect URI registered in your Zoom OAuth App and used when obtaining the authorization code."
    ),
  extra: zod
    .record(zod.string(), zod.any())
    .describe(
      "An optional free-form JSON object you can attach to the credential.\n\nThis is stored as-is and never interpreted by the API. Useful for tagging credentials with your own identifiers (e.g. internal user ID, environment, tenant) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
    )
    .or(zod.null())
    .optional()
})

export const updateZoomCredentialResponseDataCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/
export const updateZoomCredentialResponseDataNameMax = 100
export const updateZoomCredentialResponseDataLastErrorAtRegExpOne =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/
export const updateZoomCredentialResponseDataCreatedAtRegExp =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/
export const updateZoomCredentialResponseDataUpdatedAtRegExp =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|(?:02)-(?:0[1-9]|1\d|2[0-8])))T(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z))$/

export const updateZoomCredentialResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    credential_id: zod
      .string()
      .uuid()
      .regex(updateZoomCredentialResponseDataCredentialIdRegExp)
      .describe("The unique identifier (UUID) of the Zoom credential"),
    name: zod
      .string()
      .min(1)
      .max(updateZoomCredentialResponseDataNameMax)
      .describe(
        "A user-friendly name for the credential.\n\nThis name is for your reference and can be anything that helps you identify this credential. Names do not need to be unique."
      ),
    credential_type: zod
      .enum(["app", "user"])
      .describe(
        "The type of Zoom credential.\n\n- `app`: SDK-only credentials (client_id and client_secret) for joining meetings\n- `user`: User-authorized credentials with OAuth tokens for OBF token support"
      ),
    zoom_user_id: zod
      .string()
      .describe(
        "The Zoom user ID associated with this credential.\n\nOnly present for 'user' type credentials. This is the Zoom user who authorized the OAuth app."
      )
      .or(zod.null()),
    zoom_account_id: zod
      .string()
      .describe(
        "The Zoom account ID associated with this credential.\n\nOnly present for 'user' type credentials."
      )
      .or(zod.null()),
    zoom_email: zod
      .string()
      .describe(
        "The email address of the Zoom user who authorised this credential.\n\nCaptured from Zoom's `/users/me` API at the time of OAuth exchange (or refresh) using the `user:read:user` scope. Use this to display which Zoom account is connected when a user has multiple accounts.\n\nOnly present for 'user' type credentials."
      )
      .or(zod.null()),
    zoom_display_name: zod
      .string()
      .describe(
        "The display name of the Zoom user who authorised this credential.\n\nCaptured from Zoom's `/users/me` API at the time of OAuth exchange (or refresh).\n\nOnly present for 'user' type credentials."
      )
      .or(zod.null()),
    scopes: zod
      .string()
      .describe(
        "The OAuth scopes granted to this credential.\n\nOnly present for 'user' type credentials. Comma-separated list of Zoom OAuth scopes."
      )
      .or(zod.null()),
    state: zod
      .enum(["active", "invalid"])
      .describe(
        "The current state of the Zoom credential.\n\n- `active`: Credential is working and can be used\n- `invalid`: Credential has failed (tokens revoked, expired, etc.)"
      ),
    last_error_message: zod
      .string()
      .describe(
        "The last error message from OBF token fetch attempts.\n\nThis is set when a bot fails to fetch an OBF token using this credential. It is cleared when a successful OBF token fetch occurs."
      )
      .or(zod.null()),
    last_error_at: zod
      .string()
      .datetime({})
      .regex(updateZoomCredentialResponseDataLastErrorAtRegExpOne)
      .describe(
        "When the last OBF token fetch error occurred (ISO 8601).\n\nCleared when a successful OBF token fetch occurs."
      )
      .or(zod.null()),
    created_at: zod
      .string()
      .datetime({})
      .regex(updateZoomCredentialResponseDataCreatedAtRegExp)
      .describe("When this credential was created (ISO 8601)"),
    updated_at: zod
      .string()
      .datetime({})
      .regex(updateZoomCredentialResponseDataUpdatedAtRegExp)
      .describe("When this credential was last updated (ISO 8601)"),
    extra: zod
      .record(zod.string(), zod.any())
      .describe(
        "An optional free-form JSON object you can attach to the credential.\n\nThis is stored as-is and never interpreted by the API. Useful for tagging credentials with your own identifiers (e.g. internal user ID, environment, tenant) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
      )
      .or(zod.null())
  })
})

/**
 * Delete a Zoom credential (soft delete).

    The credential is marked as deleted and will no longer appear in list responses. Bots currently using this credential will fail to fetch OBF tokens.

    **Impact on Bots:** If bots are configured to use this credential (via `zoom_config.credential_id`), they will fail with an error when trying to fetch OBF tokens. Make sure to update any bot configurations before deleting a credential.

    **Soft Delete:** The credential is soft-deleted and can potentially be restored by support if needed. All associated encrypted data remains in the database.

    Returns 404 if the credential is not found or does not belong to your team.
 * @summary Delete a Zoom credential
 */
export const deleteZoomCredentialPathIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const deleteZoomCredentialParams = zod.object({
  id: zod
    .string()
    .uuid()
    .regex(deleteZoomCredentialPathIdRegExp)
    .describe("The unique identifier (UUID) of the Zoom credential")
})

export const deleteZoomCredentialResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    message: zod.string().describe("Success message")
  })
})
